import React from 'react';
import dayjs from 'dayjs';
import cookieUtil from 'tradera/utils/cookie';
import { useAppDispatch, useAppSelector } from 'tradera/state/hooks';
import { ScrollableModal } from 'tradera/components/scrollable-modal/scrollable-modal';
import { Close } from '@radix-ui/react-dialog';
import { closeModal } from 'tradera/state/ui/reducer';
import { APP_DOWNLOAD_MODAL_DISMISSED } from 'tradera/constants/cookies';
import { Heading } from 'tradera/components/atoms/heading/heading';
import { Trans, useTranslator } from 'tradera/lang/translate';
import { useAppDownloadLink } from 'tradera/hooks/use-app-download-link';
import RatingStars from 'tradera/components/rating-stars/rating-stars';
import useIsDesktop from 'tradera/hooks/use-is-desktop';
import { GtmService } from 'tradera/services/google-tagmanager-service';
import {
    AppstoreBadge,
    GooglePlayBadge
} from '../app-download-badge/app-download-badge';

import CloseIcon from 'tradera/blueprint/theme/icons2/close.svg?jsx';
import AppstoreLogo from 'tradera/blueprint/theme/logos/appstore.svg';
import GooglePlayLogo from 'tradera/blueprint/theme/logos/googleplay.svg';
import AppDownloadMobile1x from './app-download-mobile-1x.png';
import AppDownloadMobile2x from './app-download-mobile-2x.png';
import AppDownloadMobile3x from './app-download-mobile-3x.png';
import AppDownloadDesktop1x from './app-download-desktop-1x.png';
import AppDownloadDesktop2x from './app-download-desktop-2x.png';
import AppDownloadDesktop3x from './app-download-desktop-3x.png';
import QrCodeAppstore from './qr-code-appstore.png';
import QrCodeGooglePlay from './qr-code-google-play.png';

import styles from './app-download-modal.module.scss';
import cn from 'classnames';

const saveModalDismissed = () => {
    const count =
        parseInt(cookieUtil.readCookie(APP_DOWNLOAD_MODAL_DISMISSED)) || 0;
    const expiresOneYear = dayjs().add(1, 'year').toDate();
    cookieUtil.createCookie(
        APP_DOWNLOAD_MODAL_DISMISSED,
        count + 1,
        expiresOneYear
    );
};

export const APP_DOWNLOAD_MODAL_ID = 'appDownloadModalId';
export const APP_DOWNLOAD_GA_CATEGORY = 'App download modal';

export const AppDownloadModal = () => {
    const { t } = useTranslator();
    const dispatch = useAppDispatch();

    const [backgroundLoaded, setBackgroundLoaded] = React.useState(false);
    const [backgroundError, setBackgroundError] = React.useState(false);

    const { currentModal } = useAppSelector((state) => state.ui.modals);

    const isDesktop = useIsDesktop();

    const handleCloseModal = () => {
        saveModalDismissed();
        dispatch(closeModal());
        GtmService.trackAction(APP_DOWNLOAD_GA_CATEGORY, 'Dismiss');
    };

    const trackAppDownloadClick = (store: string) => {
        GtmService.trackAction(APP_DOWNLOAD_GA_CATEGORY, store);
    };

    const { appStore, googlePlay } = useAppDownloadLink();

    if (currentModal !== APP_DOWNLOAD_MODAL_ID) {
        return null;
    }

    const maxWidth = isDesktop ? '60rem' : '30rem';

    const handleImageLoaded = () => {
        setBackgroundLoaded(true);
        setBackgroundError(false);
    };

    const handleImageError = () => {
        setBackgroundLoaded(true);
        setBackgroundError(true);
    };

    return (
        <ScrollableModal
            maxWidth={maxWidth}
            onClose={handleCloseModal}
            noPadding
            show>
            <div className={styles.appDownloadModal}>
                <div className={styles.appDownloadBackground}>
                    {isDesktop && !backgroundError ? (
                        <img
                            srcSet={`${AppDownloadDesktop1x} 1x, ${AppDownloadDesktop2x} 2x, ${AppDownloadDesktop3x} 3x`}
                            src={AppDownloadDesktop2x}
                            className={cn(
                                styles.appDownloadBackgroundImage,
                                styles.appDownloadImageLoading,
                                {
                                    [styles.appDownloadBackgroundImageLoaded]:
                                        backgroundLoaded
                                }
                            )}
                            alt=""
                            onLoad={handleImageLoaded}
                            onError={handleImageError}
                        />
                    ) : !backgroundError ? (
                        <img
                            srcSet={`${AppDownloadMobile1x} 1x, ${AppDownloadMobile2x} 2x, ${AppDownloadMobile3x} 3x`}
                            src={AppDownloadMobile2x}
                            className={cn(
                                styles.appDownloadBackgroundImage,
                                styles.appDownloadImageLoading,
                                {
                                    [styles.appDownloadBackgroundImageLoaded]:
                                        backgroundLoaded
                                }
                            )}
                            alt=""
                            onLoad={handleImageLoaded}
                            onError={handleImageError}
                        />
                    ) : null}
                    <div className={styles.modalContent}>
                        <div
                            className={cn(
                                'd-flex flex-column justify-content-lg-center h-100 pt-7 px-3 pb-0 pb-md-5 ml-auto',
                                {
                                    'w-50': isDesktop
                                }
                            )}>
                            <Heading branded className="mb-3">
                                <Trans
                                    i18nKey="appDownloadModal_title_Trans"
                                    components={{
                                        break: isDesktop ? <br /> : <span />,
                                        branded: <span className="bg-branded" />
                                    }}
                                />
                            </Heading>
                            <ul className="plain-list mb-3 mb-lg-5">
                                <li className="mb-1">
                                    <Heading
                                        as="h2"
                                        branded
                                        className="font-weight-normal"
                                        size={isDesktop ? 'berlin' : 'paris'}>
                                        {t('appDownloadModal_text_1')}
                                    </Heading>
                                </li>
                                <li>
                                    <Heading
                                        as="h2"
                                        branded
                                        className="font-weight-normal"
                                        size={isDesktop ? 'berlin' : 'paris'}>
                                        {t('appDownloadModal_text_2')}
                                    </Heading>
                                </li>
                            </ul>
                            <div
                                className={cn(
                                    `d-flex mb-3`,
                                    styles.appDownloadImageLoading,
                                    {
                                        [styles.appDownloadBackgroundImageLoaded]:
                                            backgroundLoaded
                                    }
                                )}>
                                <div className={cn('d-flex flex-column')}>
                                    {isDesktop && (
                                        <img
                                            className="mb-2"
                                            src={QrCodeAppstore}
                                            loading="lazy"
                                            height={92}
                                            width={92}
                                            alt={t(
                                                'footer_downloadFromAppStore',
                                                {
                                                    appName: 'App Store'
                                                }
                                            )}
                                        />
                                    )}
                                    <a
                                        href={appStore}
                                        rel="noreferrer"
                                        target="_blank"
                                        onClick={() =>
                                            trackAppDownloadClick('App Store')
                                        }>
                                        {isDesktop ? (
                                            <img
                                                src={AppstoreLogo}
                                                loading="lazy"
                                                height="20"
                                                title={t(
                                                    'footer_downloadFromAppStore',
                                                    {
                                                        appName: 'App Store'
                                                    }
                                                )}
                                                className="icon-dark mr-7"
                                                alt={t(
                                                    'footer_downloadFromAppStore',
                                                    {
                                                        appName: 'App Store'
                                                    }
                                                )}
                                            />
                                        ) : (
                                            <AppstoreBadge
                                                height="32"
                                                className="mr-2"
                                            />
                                        )}
                                    </a>
                                </div>
                                <div className="d-flex flex-column">
                                    {isDesktop && (
                                        <img
                                            className="mb-2"
                                            src={QrCodeGooglePlay}
                                            loading="lazy"
                                            height={92}
                                            width={92}
                                            alt={t(
                                                'footer_downloadFromAppStore',
                                                {
                                                    appName: 'Google Play'
                                                }
                                            )}
                                        />
                                    )}
                                    <a
                                        href={googlePlay}
                                        rel="noreferrer"
                                        target="_blank"
                                        onClick={() =>
                                            trackAppDownloadClick('Google Play')
                                        }>
                                        {isDesktop ? (
                                            <img
                                                src={GooglePlayLogo}
                                                loading="lazy"
                                                height="20"
                                                title={t(
                                                    'footer_downloadFromAppStore',
                                                    {
                                                        appName: 'Google Play'
                                                    }
                                                )}
                                                className="icon-dark"
                                                alt={t(
                                                    'footer_downloadFromAppStore',
                                                    {
                                                        appName: 'Google Play'
                                                    }
                                                )}
                                            />
                                        ) : (
                                            <GooglePlayBadge height="32" />
                                        )}
                                    </a>
                                </div>
                            </div>
                            <div>
                                <RatingStars rating={4.7} dark />
                            </div>
                        </div>
                    </div>
                </div>
                <Close className={`btn btn-round ${styles.closeButton}`}>
                    <CloseIcon className="icon" />
                </Close>
            </div>
        </ScrollableModal>
    );
};
